import moment from 'moment'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type {
  InsuranceClaimsPaymentPostingSummary,
  InsuranceClaimsOutstandingSummary,
  InsuranceClaimsPipelineRow,
  InsuranceClaimsResult,
} from '@services/api/dashboard/operations.api'
import type { SchedulingChartConfig } from '@pages/Dashboard/SchedulingPerformanceDetail/schedulingPerformanceDetail.mockData'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import {
  computeChartAxis,
  filterYtdPeriodItems,
  formatCurrency,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import { PeriodBucket } from '@services/api/dashboard/finance.api'

// --- Shared helpers ---

const formatDays = (days: number): string =>
  days === 1 ? '1 day' : `${Math.round(days)} days`

const formatDisplayDate = (iso: string | null): string => {
  if (!iso) return '—'
  return moment(iso).format('MMM D, YYYY')
}

// --- Insurance claims ---

export type InsuranceClaimsDetailTableRow = {
  id: string
  patientId: string
  patientName: string
  payer: string
  claimId: string
  dateOfService: string
  procedure: string
  amount: string
  dateSubmitted: string
  status: string
}

export function mapInsuranceClaimsOutstandingMetrics(
  summary: InsuranceClaimsOutstandingSummary | undefined,
  loading: boolean,
): DashboardMetricItemData[] {
  if (loading || !summary) {
    return [
      {
        id: 'ins-open-claims',
        leftLabel: 'Open claims',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-submitted',
        leftLabel: 'Submitted',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-pending-payer',
        leftLabel: 'Pending payer',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-outstanding-balance',
        leftLabel: 'Outstanding balance',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-avg-days-outstanding',
        leftLabel: 'Avg. days outstanding',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-appeals-open',
        leftLabel: 'Appeals open',
        value: HYGIENE_PLACEHOLDER,
      },
    ]
  }

  return [
    {
      id: 'ins-open-claims',
      leftLabel: 'Open claims',
      value: String(summary.openClaims),
    },
    {
      id: 'ins-submitted',
      leftLabel: 'Submitted',
      value: formatCurrency(summary.submittedBalance, { compact: true }),
    },
    {
      id: 'ins-pending-payer',
      leftLabel: 'Pending payer',
      value: String(summary.pendingPayers),
    },
    {
      id: 'ins-outstanding-balance',
      leftLabel: 'Outstanding balance',
      value: formatCurrency(summary.outstandingBalance, { compact: true }),
    },
    {
      id: 'ins-avg-days-outstanding',
      leftLabel: 'Avg. days outstanding',
      value: formatDays(summary.averageDaysOutstanding),
    },
    {
      id: 'ins-appeals-open',
      leftLabel: 'Appeals open',
      value: String(summary.appealsOpen),
    },
  ]
}

export function mapInsuranceClaimsPaymentPostingMetrics(
  summary: InsuranceClaimsPaymentPostingSummary | undefined,
  loading: boolean,
): DashboardMetricItemData[] {
  if (loading || !summary) {
    return [
      {
        id: 'ins-avg-days-to-pay',
        leftLabel: 'Avg. days to pay',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-avg-days-to-post',
        leftLabel: 'Avg. days to post',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-unposted-insurance',
        leftLabel: 'Unposted insurance',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-est-collections',
        leftLabel: 'Est. insurance collections',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'ins-contractual-adj',
        leftLabel: 'Contractual adjustments',
        value: HYGIENE_PLACEHOLDER,
      },
    ]
  }

  return [
    {
      id: 'ins-avg-days-to-pay',
      leftLabel: 'Avg. days to pay',
      value: formatDays(summary.averageDaysToPay),
    },
    {
      id: 'ins-avg-days-to-post',
      leftLabel: 'Avg. days to post',
      value: formatDays(summary.averageDaysToPost),
    },
    {
      id: 'ins-unposted-insurance',
      leftLabel: 'Unposted insurance',
      value: formatCurrency(summary.unpostedInsurance, { compact: true }),
    },
    {
      id: 'ins-est-collections',
      leftLabel: 'Est. insurance collections',
      value: formatCurrency(summary.estimatedInsuranceCollection, {
        compact: true,
      }),
    },
    {
      id: 'ins-contractual-adj',
      leftLabel: 'Contractual adjustments',
      value: formatCurrency(summary.contractualAdjustments, { compact: true }),
    },
  ]
}

export function mapInsuranceClaimsVolumeChartConfig(
  range: RangeKey,
  date: string,
  buckets: PeriodBucket[] | undefined,
): SchedulingChartConfig {
  const filtered = filterYtdPeriodItems(
    range,
    date,
    buckets ?? [],
    (b) => b.label,
  )
  const data = filtered.map((bucket, index) => ({
    label: bucket.label,
    value: bucket.value,
    isCurrent: index === filtered.length - 1,
  }))
  const axis = computeChartAxis(data.map((point) => point.value))
  return {
    data,
    yDomain: axis.yDomain,
    yTicks: axis.yTicks,
  }
}

export function mapInsuranceClaimsPipelineRows(
  rows: InsuranceClaimsPipelineRow[] | undefined,
): InsuranceClaimsDetailTableRow[] {
  return (rows ?? []).map((row, index) => ({
    id: String(index + 1),
    patientId: row.patientId,
    patientName: row.patientName,
    payer: row.payer,
    claimId: row.claimId,
    dateOfService: formatDisplayDate(row.dateOfService),
    procedure: row.procedure,
    amount: formatCurrency(row.amount, { compact: true }),
    dateSubmitted: formatDisplayDate(row.dateSubmitted),
    status: row.status,
  }))
}

export type InsuranceClaimsMappedData = {
  claimsOutstandingMetrics: DashboardMetricItemData[]
  paymentPostingMetrics: DashboardMetricItemData[]
  volumeChartConfig: SchedulingChartConfig
  pipelineRows: InsuranceClaimsDetailTableRow[]
}

export function mapInsuranceClaimsResult(
  data: InsuranceClaimsResult | null,
  loading: boolean,
  range: RangeKey,
  date: string,
): InsuranceClaimsMappedData {
  return {
    claimsOutstandingMetrics: mapInsuranceClaimsOutstandingMetrics(
      data?.claimsOutstanding,
      loading,
    ),
    paymentPostingMetrics: mapInsuranceClaimsPaymentPostingMetrics(
      data?.paymentPosting,
      loading,
    ),
    volumeChartConfig: mapInsuranceClaimsVolumeChartConfig(
      range,
      date,
      data?.claimsFiledVolume,
    ),
    pipelineRows: mapInsuranceClaimsPipelineRows(data?.appointmentPipeline),
  }
}
